/**
 * Shared validation for "which uploaded file is which" — used by every tool that hands pending files
 * to the processing pipeline. Three independent checks, in the order the tools apply them:
 *
 *  1. confidence      — can we tell the roles from the filenames alone, or must the teacher confirm?
 *  2. missing files   — does every declared filename actually exist in the exam's pending files?
 *  3. proposal drift  — does the declared assignment contradict the last proposal the teacher saw?
 *
 * All user-facing text goes through i18next so it follows the conversation language.
 */

import i18next from 'i18next';
import '../../../i18n.js';
import { AssignmentProposalMessage } from '../../conversation.js';
import type { Exam, ExamState } from '../../exam.js';
import { StepState } from '../../extraction-progress.js';
import { createPendingFileResolver, normalizeFileName, type UploadRole } from '../file-intake.js';

export { normalizeFileName };

// --- what the exam's state allows ----------------------------------------------------------------

/**
 * Whether structure extraction is already under way or done. Deliberately the same condition
 * `startStructureExtractionMutation` uses to decide whether it may (re)start, so a failed extraction
 * (ERROR) counts as not started and can be retried.
 */
export function structureExtractionStarted(examState: ExamState | null | undefined): boolean {
  const status = examState?.taskExtractionProgress?.status;
  return status === StepState.IN_PROGRESS || status === StepState.COMPLETED;
}

/**
 * Whether a blank exam can still be used. It is only ever read as the SOURCE for structure
 * extraction, so it becomes worthless the moment that extraction runs.
 */
export function canProcessBlankExam(examState: ExamState | null | undefined): boolean {
  return !structureExtractionStarted(examState);
}

/**
 * Whether the exam has anything the structure can be derived from. The blank exam is the preferred
 * source; failing that, the first submission is used, which is why an exam with submissions can reach
 * a structure without one. With neither, an uploaded master solution or grading scheme is stored but
 * cannot be extracted yet — the agent has to say so instead of promising a result.
 */
export function hasStructureSource(exam: Exam | null | undefined): boolean {
  return (
    structureExtractionStarted(exam?.examState) ||
    (Array.isArray(exam?.blankExamIds) && exam.blankExamIds.length > 0) ||
    (exam?.examState?.uploadProgress?.submissionsFound ?? 0) > 0
  );
}

/**
 * Whether the teacher has confirmed the task structure — the precondition for extracting a master
 * solution or grading scheme, since both are matched against the confirmed tasks and the teacher may
 * still be editing them until then. (The flag is only ever set on a structure that exists, so it does
 * not need to re-check extraction status; that also keeps it true when description extraction failed
 * and left the task step in ERROR.)
 *
 * This is what makes an upload a "re-upload": with the gate already open, a new master solution or
 * grading scheme is extracted immediately instead of waiting for the confirmation to release it.
 */
export function isStructureConfirmed(examState: ExamState | null | undefined): boolean {
  return examState?.structureConfirmationState?.confirmed === true;
}

/** A file→role assignment as supplied by the agent. Only submissions can hold more than one file. */
export interface FileAssignment {
  submissions: string[];
  masterSolution?: string;
  gradingScheme?: string;
  blankExam?: string;
}

export interface AssignmentEntry {
  name: string;
  role: UploadRole;
}

/**
 * Parses the raw tool arguments (German field names, as the agent sees them) into a normalized
 * assignment. `allowBlankExam` mirrors the tool contract: a blank exam can only be supplied while the
 * exam's structure hasn't been derived yet, so tools that run later don't accept the field at all.
 */
export function parseAssignmentArgs(toolCallArgs: any, options?: { allowBlankExam?: boolean }): FileAssignment {
  const single = (value: unknown) => (typeof value === 'string' && value.trim() ? value : undefined);

  return {
    submissions: Array.isArray(toolCallArgs?.Schülerabgaben) ? toolCallArgs.Schülerabgaben : [],
    masterSolution: single(toolCallArgs?.Musterlösung),
    gradingScheme: single(toolCallArgs?.Bewertungsschema),
    blankExam: options?.allowBlankExam ? single(toolCallArgs?.LeereKlausur) : undefined,
  };
}

/** Flattens the assignment into (filename, role) pairs, skipping blanks. */
export function assignmentEntries(assignment: FileAssignment): AssignmentEntry[] {
  const entries: AssignmentEntry[] = [];

  for (const name of assignment.submissions) {
    if (typeof name === 'string' && name.trim()) entries.push({ name, role: 'submissions' });
  }
  if (assignment.masterSolution) entries.push({ name: assignment.masterSolution, role: 'masterSolution' });
  if (assignment.gradingScheme) entries.push({ name: assignment.gradingScheme, role: 'gradingScheme' });
  if (assignment.blankExam) entries.push({ name: assignment.blankExam, role: 'blankExam' });

  return entries;
}

/**
 * The pending entry ids an assignment claims — resolved exactly the way `processUploadedFiles` will
 * resolve them (same resolver, same role order), so duplicate filenames map to the same entries here
 * as they will there. Used to protect the files a call assigns from a cancellation in the same call.
 */
export function collectAssignedPendingIds(assignment: FileAssignment, pendingFiles: any[]): Set<string> {
  const resolver = createPendingFileResolver(pendingFiles, { parsePendingIdTokens: true });
  resolver.resolveOne(assignment.blankExam);
  resolver.resolveOne(assignment.masterSolution);
  resolver.resolveOne(assignment.gradingScheme);
  resolver.resolveMany(assignment.submissions);
  return resolver.usedPendingIds;
}

/** Localized role name for user-facing messages. */
export function roleLabel(role: UploadRole): string {
  switch (role) {
    case 'submissions':
      return i18next.t('upload.categorySubmissions', { defaultValue: 'Schülerabgaben' });
    case 'masterSolution':
      return i18next.t('upload.categoryMasterSolution', { defaultValue: 'Musterlösung' });
    case 'gradingScheme':
      return i18next.t('upload.categoryGradingScheme', { defaultValue: 'Bewertungsschema' });
    case 'blankExam':
      return i18next.t('upload.categoryBlankExam', { defaultValue: 'Leere Klausur' });
  }
}

// --- 1. confidence -------------------------------------------------------------------------------

/** Below this we ask the teacher to confirm the assignment instead of trusting the filenames. */
export const ASSIGNMENT_CONFIDENCE_THRESHOLD = 0.9;

/** How well a filename supports the role it was assigned to. */
export function calculateAssignmentConfidence(fileName: string, role: UploadRole): number {
  const lowerName = String(fileName || '').toLowerCase();

  switch (role) {
    case 'masterSolution':
      if (/(lösung|loesung|muster|solution|antwort)/.test(lowerName)) return 0.95;
      return 0.55;

    case 'gradingScheme':
      if (/(bewertung|schema|erwartungshorizont|grading|punkte|kriterien)/.test(lowerName)) return 0.95;
      return 0.55;

    case 'blankExam':
      if (/(leer|blank|angabe|aufgabe|klausur)/.test(lowerName)) return 0.85;
      return 0.6;

    case 'submissions':
      if (/(scan|abgabe|student|schüler|klausur_|img_|dokument)/.test(lowerName)) return 0.9;
      return 0.8;
  }
}

export interface LowConfidenceAssignment extends AssignmentEntry {
  score: number;
}

/** Assignments whose filename doesn't clearly support the role. Empty = safe to process directly. */
export function findLowConfidenceAssignments(
  assignment: FileAssignment,
  threshold = ASSIGNMENT_CONFIDENCE_THRESHOLD,
): LowConfidenceAssignment[] {
  return assignmentEntries(assignment)
    .map(entry => ({ ...entry, score: calculateAssignmentConfidence(entry.name, entry.role) }))
    .filter(entry => entry.score < threshold);
}

/** `'file.pdf' (geplant als Musterlösung), …` — the list embedded in the confirmation prompts. */
export function formatLowConfidenceList(lowConfidence: LowConfidenceAssignment[]): string {
  return lowConfidence
    .map(entry =>
      i18next.t('upload.lowConfidenceListFormat', {
        file: entry.name,
        category: roleLabel(entry.role),
        defaultValue: `'${entry.name}' (geplant als ${roleLabel(entry.role)})`,
      }),
    )
    .join(', ');
}

// --- 2. missing files ----------------------------------------------------------------------------

/** Normalized names of every pending file on the exam. */
export function listPendingFileNames(pendingFiles: any[]): string[] {
  return (Array.isArray(pendingFiles) ? pendingFiles : []).map(file => normalizeFileName(file?.name)).filter(Boolean);
}

/** Declared filenames that aren't in the exam's pending files — the agent invented or mistyped them. */
export function findMissingPendingFiles(assignment: FileAssignment, pendingFiles: any[]): string[] {
  const available = new Set(listPendingFileNames(pendingFiles));
  return assignmentEntries(assignment)
    .map(entry => entry.name)
    .filter(name => !available.has(normalizeFileName(name)));
}

/**
 * Names the agent got wrong, next to the names that ARE there. Without the second list the agent has no
 * way to tell a typo from a genuinely absent file, and it asks the teacher to upload something the exam
 * may already have — so the available names are always listed, and re-uploading is ruled out explicitly.
 */
export function formatMissingFilesBlock(missingFiles: string[], availableFiles: string[] = []): string {
  const available = availableFiles.length
    ? i18next.t('upload.filesMissingAvailable', {
        files: availableFiles.join(', '),
        defaultValue: `Im Upload-Puffer liegen aktuell: ${availableFiles.join(
          ', ',
        )}. Rufe das Tool erneut mit genau diesen Namen auf. Ein Name, der hier fehlt, wurde in der Regel schon verarbeitet und ist auf der Klausur gespeichert — fordere ihn NICHT erneut vom Nutzer an.`,
      })
    : i18next.t('upload.filesMissingBufferEmpty', {
        defaultValue:
          'Der Upload-Puffer ist leer — es gibt derzeit keine unverarbeitete Datei. Bereits verarbeitete Dateien bleiben auf der Klausur gespeichert und dürfen NICHT erneut angefordert werden; prüfe zuerst den Status der Klausur, bevor du den Nutzer um irgendeinen Upload bittest.',
      });

  return i18next.t('upload.filesMissing', {
    files: missingFiles.join(', '),
    available,
    defaultValue: `SYSTEM BLOCK: Diese Dateinamen wurden im Upload-Puffer nicht gefunden: ${missingFiles.join(
      ', ',
    )}. ${available}`,
  });
}

// --- 3. proposal drift ---------------------------------------------------------------------------

/**
 * The role each filename carried in the most recent assignment proposal the teacher saw. Messages are
 * scanned newest-first and the first hit wins, so a later proposal supersedes an earlier one.
 *
 * With `allowBlankExam` off, blank-exam entries are not indexed at all: a tool that can't accept a
 * blank exam must not flag a file as "drifted" just because an old proposal put it in that role.
 */
export function buildProposalRoleMap(
  messages: any[],
  options?: { allowBlankExam?: boolean },
): Record<string, UploadRole> {
  const roleMap: Record<string, UploadRole> = {};
  const claim = (rawName: unknown, role: UploadRole) => {
    const name = normalizeFileName(rawName);
    if (!name || roleMap[name]) return;
    roleMap[name] = role;
  };

  for (const message of (Array.isArray(messages) ? messages : []).slice().reverse()) {
    if (message?._class !== AssignmentProposalMessage._class) continue;

    if (Array.isArray(message.schuelerabgaben)) {
      for (const name of message.schuelerabgaben) claim(name, 'submissions');
    }
    claim(message.musterloesung, 'masterSolution');
    claim(message.bewertungsschema, 'gradingScheme');
    if (options?.allowBlankExam) claim(message.leereKlausur, 'blankExam');
  }

  return roleMap;
}

export interface AssignmentMismatch {
  name: string;
  proposed: string;
  supplied: string;
}

/** Files the agent assigned to a different role than the last proposal the teacher confirmed. */
export function findAssignmentMismatches(
  assignment: FileAssignment,
  roleMap: Record<string, UploadRole>,
): AssignmentMismatch[] {
  const mismatches: AssignmentMismatch[] = [];

  for (const entry of assignmentEntries(assignment)) {
    const proposedRole = roleMap[normalizeFileName(entry.name)];
    if (!proposedRole || proposedRole === entry.role) continue;
    mismatches.push({ name: entry.name, proposed: roleLabel(proposedRole), supplied: roleLabel(entry.role) });
  }

  return mismatches;
}

export function formatMismatchBlock(mismatches: AssignmentMismatch[]): string {
  const lines = mismatches.map(mismatch =>
    i18next.t('upload.mismatchFormat', {
      name: mismatch.name,
      proposed: mismatch.proposed,
      supplied: mismatch.supplied,
      defaultValue: `- ${mismatch.name}: zuletzt vorgeschlagen als "${mismatch.proposed}", jetzt übergeben als "${mismatch.supplied}"`,
    }),
  );

  return i18next.t('upload.assignmentMismatch', {
    lines: lines.join('\n'),
    defaultValue: `SYSTEM BLOCK: Die folgenden Dateizuordnungen stimmen nicht mit dem letzten Vorschlag überein. Bitte rufe send_assignment_confirmation_message auf und bestätige die neue Zuweisung mit dem Nutzer.\n${lines.join(
      '\n',
    )}`,
  });
}
